import { SaveOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button, Card, Col,
  Row,
  Spin,
  Switch,
  Typography,
  message
} from 'antd';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownElement } from 'common/components/DropdownType';
import ErrorText from 'common/components/ErrorText';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import { createExternalManagerService, getExternalManagerbyIdService, updateExternalManagerService } from 'common/services/externalManager';
import { ExternalManagerRequestTypes } from 'common/services/externalManager/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { externalManagerSchema } from 'common/utils/schemas';

export type ExternalManagerFormTypes = {
  name: string;
  kioskCode?: OptionType;
  isActive: boolean;
};

const defaultValues: ExternalManagerFormTypes = {
  name: '',
  isActive: false,
};

const ExternalManagerDetail: React.FC<ActiveRoles> = ({ roleCreate, roleUpdate }) => {
  /* Hooks */
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /* States */
  const idParams = Number(searchParams.get('id'));

  /* React-hook-form */
  const method = useForm<ExternalManagerFormTypes>({
    resolver: yupResolver(externalManagerSchema),
    defaultValues,
    mode: 'onChange',
  });

  /* Queries */
  const { isLoading } = useQuery(
    ['external-manager-detail', idParams],
    () => {
      if (idParams) {
        return getExternalManagerbyIdService(idParams);
      }
      return undefined;
    },
    {
      enabled: !!idParams,
      onSuccess: (data) => {
        method.reset({
          name: data?.externalManagerData.name,
          kioskCode: {
            label: data?.kioskData.name,
            value: data?.kioskData.code,
          },
          isActive: data?.externalManagerData.isActive,
        });
      }
    }
  );

  const { mutate: updateExternalManagerMutate, isLoading: updateLoading } = useMutation(
    ['external-manager-update'],
    async (params: ExternalManagerRequestTypes
      & { id: number }) => updateExternalManagerService(params.id, params),
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: () => {
        message.error(t('message.updateError'));
      }
    }
  );

  const { mutate: creatExternalManagerMutate, isLoading: createLoading } = useMutation(
    ['external-manager-create'],
    async (params: ExternalManagerRequestTypes) => createExternalManagerService(params),
    {
      onSuccess: () => {
        message.success(t('message.createSuccess'));
        navigate(`${ROUTE_PATHS.EXTERNAL_MANAGER}`);
      },
      onError: () => {
        message.error(t('message.createError'));
      }
    }
  );

  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();
    const submitData = {
      name: formData.name,
      kioskCode: String(formData.kioskCode?.value),
      isActive: formData.isActive,
    };

    if (idParams) {
      updateExternalManagerMutate({
        ...submitData,
        id: idParams
      });
    } else {
      creatExternalManagerMutate(submitData);
    }
  };

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={idParams ? t('system.update') : t('system.create')}
        rightHeader={(
          <Button
            type="primary"
            disabled={(idParams && !roleUpdate) || (!idParams && !roleCreate)}
            loading={updateLoading || createLoading}
            onClick={() => onSubmit()}
          >
            <SaveOutlined />
            {t('system.save')}
          </Button>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <Spin size="large" spinning={updateLoading || isLoading || createLoading}>
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col>
                <Card type="inner">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Typography.Text strong>
                        {t('system.name')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                      <Controller
                        name="name"
                        render={({
                          field: { onChange, value },
                          fieldState: { error },
                        }) => (
                          <div className="u-mt-8">
                            <Input
                              onChange={onChange}
                              value={value}
                              placeholder={t('system.name')}
                              error={error?.message}
                            />
                          </div>
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <Typography.Text strong>
                        {t('kioskManagement.kioskIds')}
                        {' '}
                      </Typography.Text>
                      <Typography.Text strong type="danger">
                        *
                      </Typography.Text>
                      <Controller
                        name="kioskCode"
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <div className="u-mt-8">
                            <DropdownElement
                              type="kiosk"
                              placeholder={`${t('system.select')} kiosk`}
                              locale="vi"
                              value={value}
                              onChange={onChange}
                              isShowSearch
                              isValueSlug
                              isGetOption
                            />
                            {
                              (error as any)?.value && (
                                <ErrorText>
                                  {(error as any)?.value?.message}
                                </ErrorText>
                              )
                            }
                          </div>
                        )}
                      />
                    </Col>
                    <Col span={12}>
                      <div>
                        <Typography.Text strong>
                          {t('externalManager.active')}
                          {' '}
                        </Typography.Text>
                        <Controller
                          name="isActive"
                          render={({ field }) => (
                            <div className="u-mt-8">
                              <Switch
                                checked={field.value}
                                onChange={field.onChange}
                              />
                            </div>
                          )}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </FormProvider>
        </Spin>
      </div>
    </>
  );
};

export default ExternalManagerDetail;
